import {TitleCasePipe} from './../pipes/title-case.pipe';
import {Injectable} from '@angular/core';
import {SelectItem, TreeNode} from 'primeng/api';
import {isNullOrUndefined} from "@qntm-code/utils";
import {Format} from '../enums/format.enum';
import {DateService} from './date.service';
import {DatePipe} from '@angular/common';
import {LabelChange} from "@app/shared/classes/label-change";

@Injectable()
export class CommonTransformationService {

  constructor(private titleCase: TitleCasePipe, private datePipe: DatePipe, private dateService: DateService) {
  }

  convertEnumToDropDown(enumObject: any, noTitleCase?: boolean, noTitleCaseList?: string[], setLabelToValue?: boolean, excludedObjects?: any [], noSort?: boolean): SelectItem[] {
    let items: SelectItem[] = [];
    let itemsWithNumberValue = [];

    for (let index in enumObject) {
      if (typeof enumObject[index] == 'number') {
        itemsWithNumberValue.push(index);
      }
    }

    for (let index in enumObject) {
      // Skip if any functions are included in the enum namespace.
      if (typeof enumObject[index] == 'function') {
        continue;
      }
      if (!itemsWithNumberValue.find(item => item == enumObject[index])) {
        let label;
        if (setLabelToValue) {
          label = enumObject[index];
        } else {
          label = String(index);
        }
        let value = enumObject[index];
        if (!noTitleCase || noTitleCaseList && noTitleCaseList.length > 0) {
          if (setLabelToValue) {
            label = this.titleCase.transform(label, noTitleCaseList);
          } else {
            label = this.titleCase.transform(index, noTitleCaseList);
          }
        }
        items.push({label: label, value: value});
      } else {
        console.debug(`skipping duplicate number value : ${index} ${enumObject[index]}`);
      }
    }

    if (excludedObjects) {
      let filteredOptions = this.filterExcludedOptionsFromDropdown(items, excludedObjects);
      return filteredOptions;
    }

    return noSort ? items : items.sort((a, b) => (a.label < b.label ? -1 : 1));
  }

  convertEnumValueToDropdown(enumObject: any, noTitleCase?: boolean, noTitleCaseList?: string[], excludedObjects?: any [], noSort?: boolean): SelectItem[] {
    let items: SelectItem[] = [];
    let itemsWithNumberValue = [];

    for (let index in enumObject) {
      if (typeof enumObject[index] == 'number') {
        itemsWithNumberValue.push(index);
      }
    }

    for (let index in enumObject) {
      if (!itemsWithNumberValue.find(item => item == enumObject[index])) {
        let label = String(enumObject[index]);
        let value = enumObject[index];
        if (!noTitleCase || noTitleCaseList && noTitleCaseList.length > 0) {
          label = this.titleCase.transform(label, noTitleCaseList);
        }
        items.push({label: label, value: value});
      } else {
        console.debug(`skipping duplicate number value : ${index} ${enumObject[index]}`);
      }
    }

    if (excludedObjects) {
      let filteredOptions = this.filterExcludedOptionsFromDropdown(items, excludedObjects);
      return filteredOptions;
    }

    return noSort ? items : items.sort((a, b) => (a.label < b.label ? -1 : 1));
  }

  convertMapToDropDown(map: Map<any, any>): SelectItem[] {
    let items: SelectItem[] = [];

    map.forEach((value: any, key: any) => {
      items.push({label: key, value: value});
    });
    return items;
  }

  convertArrayToDropDown(objectArray: any, noTitleCase?: boolean, noTitleCaseList?: string[], enumToMap?): SelectItem[] {
    let items: SelectItem[] = [];
    if (Array.isArray(objectArray)) {
      objectArray.forEach(obj => {
        if (noTitleCase && !noTitleCaseList) {
          items.push({label: obj, value: obj});
        } else {
          items.push({label: this.titleCase.transform(obj, noTitleCaseList, enumToMap), value: obj});
        }
      });
    } else {
      console.warn("Only array objects can be converted to drop downs. Please pass in an array object.");
    }
    return items;
  }

  /**
   * This function is used to convert any object to a SelectItem array.
   * @param labelProperty is used to specify a property in an object that will be used as the label.
   * @param object the object that will provide the value and label for the drop downs.
   * @param valueProperty an optional field that specifies the property to use for the value. If omitted, the whole object will be used as the value.
   * @param noTitleCase do not title case the label
   * @param noTitleCaseList if specified, do not title case name(s) specified in this list
   * @param labelOrValueToExclude if specified, matching label or value specified in this list will be excluded
   * @param noSort by default, the list in the dropdown will be sorted
   * @param labelChangeList if specified, find and replace the label using this list
   */
  convertToDropDown(labelProperty: string, object: any, valueProperty?: string, noTitleCase?: boolean, noTitleCaseList?: string[], labelOrValueToExclude?: string[],
                    noSort?: boolean, labelChangeList?: LabelChange[]): SelectItem[] {
    let items: SelectItem[] = [];
    if (Array.isArray(object)) {

      // label or value to exclude form the dropdown
      if (labelOrValueToExclude && labelOrValueToExclude.length > 0) {
        labelOrValueToExclude.forEach(value => {
          let enumIndex = object.findIndex(obj => obj.labelProperty && obj.valueProperty && (value == obj.labelProperty || value == obj.valueProperty));
          object.splice(enumIndex, 1);
        });
      }

      object.forEach(obj => {
        if (obj.hasOwnProperty(labelProperty)) {
          if (isNullOrUndefined(valueProperty)) { // valueProperty not specified, set entire object as the value
            if (!isNullOrUndefined(obj[labelProperty])) {
              let labelFromChangeList = this.getReplaceLabel(labelChangeList, obj[labelProperty]);
              if (labelFromChangeList) {  // use the label from the labelChangeList
                items.push({label: labelFromChangeList, value: obj});
              } else {
                if (noTitleCase && !noTitleCaseList) {
                  items.push({label: obj[labelProperty], value: obj});
                } else {
                  items.push({label: this.titleCase.transform(obj[labelProperty].toString(), noTitleCaseList), value: obj});
                }
              }
            } else {
              console.warn(`The label property (obj[labelProperty]) is not available on the object. Skipping object =`);
              console.warn(obj);
            }
          } else if (obj.hasOwnProperty(valueProperty)) {  // valueProperty is specified, use the obj[valueProperty] as the value
            if (!isNullOrUndefined(obj[labelProperty])) {
              let labelFromChangeList = this.getReplaceLabel(labelChangeList, obj[labelProperty]);
              if (labelFromChangeList) {
                items.push({label: labelFromChangeList, value: obj[valueProperty]});
              } else {
                if (noTitleCase && !noTitleCaseList) {
                  items.push({label: obj[labelProperty], value: obj[valueProperty]});
                } else {
                  items.push({label: this.titleCase.transform(obj[labelProperty].toString(), noTitleCaseList), value: obj[valueProperty]});
                }
              }
            } else {
              console.warn(`The label property (obj[labelProperty]) is not available on the object. Skipping object =`);
              console.warn(obj);
            }
          } else {
            items = [];
            console.warn(`The value property '${valueProperty}' is not available on the object.`);
          }
        } else {
          items = [];
          console.warn(`The label property '${labelProperty}' is not available on the object.`);
        }
      });
    } else {
      console.warn("Only array objects can be converted to drop downs. Please pass in an array object.");
    }
    return noSort ? items : items.sort((a, b) => (a.label < b.label ? -1 : 1));
  }

  convertToLongLabelDropDown(labelProperties: string[], object: any, valueProperty?: string, noTitleCase?: boolean, separator?: string, surroundWithParentheses?: boolean): SelectItem[] {
    let items: SelectItem[] = [];

    if (Array.isArray(object)) {
      object.forEach(obj => {
        if (this.hasOwnProperties(obj, labelProperties)) {
          let combinedLabel;
          let index = 0;
          labelProperties.forEach(labelProperty => {
            if (!isNullOrUndefined(combinedLabel)) {
              if (!isNullOrUndefined(obj[labelProperty])) {
                if (index == 1 && surroundWithParentheses) {
                  combinedLabel += " (" + obj[labelProperty];
                } else {
                  combinedLabel += (separator ? separator : " : ") + obj[labelProperty];
                }
              }
            } else {
              if (!isNullOrUndefined(obj[labelProperty])) {
                combinedLabel = obj[labelProperty];
              }
            }
            index++;
          });
          if (surroundWithParentheses) {
            combinedLabel += ")";
          }
          if (isNullOrUndefined(valueProperty)) {
            if (noTitleCase) {
              items.push({label: combinedLabel, value: obj});
            } else {
              items.push({label: this.titleCase.transform(combinedLabel), value: obj});
            }
          } else if (obj.hasOwnProperty(valueProperty)) {
            if (noTitleCase) {
              items.push({label: combinedLabel, value: obj[valueProperty]});
            } else {
              items.push({label: this.titleCase.transform(combinedLabel), value: obj[valueProperty]});
            }
          } else {
            items = [];
            console.warn(`The value property '${valueProperty}' is not available on the object.`);
          }
        } else {
          items = [];
          console.warn(`The label property '${labelProperties}'are not available on the object.`);
        }
      });
    } else {
      console.warn("Only array objects can be converted to drop downs. Please pass in an array object.");
    }

    return items;
  }

  convertCalendarToPeriodDropdown(objArray: any, valueIsObject?: boolean, sortDescending?: boolean): SelectItem[] {
    let periods: SelectItem[] = [];

    if (Array.isArray(objArray)) {

      // sort based on the id otherwise sorted based on the name causes issue where Period 10 is put between Period 1 and Period 2
      let sortedPeriods;
      if (sortDescending) {
        sortedPeriods = objArray.sort((a, b) => b.id - a.id);
      } else {
        sortedPeriods = objArray.sort((a, b) => a.id - b.id);
      }

      for (let period of sortedPeriods) {
        let startOnInUTC = this.datePipe.transform(this.dateService.convertStringDateToUTCDate(period.start), Format.TimesheetDate, Format.TimeZone);
        let endOnInUTC = this.datePipe.transform(this.dateService.convertStringDateToUTCDate(period.end), Format.TimesheetDate, Format.TimeZone);

        if (valueIsObject) {
          periods.push({label: period.title + ": " + startOnInUTC + ' to ' + endOnInUTC, value: period});
        } else {
          periods.push({label: period.title + ": " + startOnInUTC + ' to ' + endOnInUTC, value: period.id});
        }
      }
    } else {
      console.warn("Only array objects can be converted to drop downs. Please pass in an array object.");
    }
    return periods;
  }

  hasOwnProperties(object, properties) {
    if (!Array.isArray(properties)) {
      return false;
    }
    properties.forEach(property => {
      if (!object.hasOwnProperty(property)) {
        return false;
      }
    });
    return true;
  }

  convertToLongLabelTree(labelProperties: string[], dataObjects: any,  separator?: string): TreeNode[] {
    let fullTree: TreeNode[] = [];

    if (Array.isArray(dataObjects)) {

      dataObjects.forEach(obj => {
        let combinedLabel;

        labelProperties.forEach(labelProperty => {
          if (!isNullOrUndefined(combinedLabel)) {
            if (!isNullOrUndefined(obj[labelProperty])) {
              combinedLabel += (separator ? separator : " : ") + obj[labelProperty];
            }
          } else {
            if (!isNullOrUndefined(obj[labelProperty])) {
              combinedLabel = obj[labelProperty];
            }
          }
        });
          let treeNode: TreeNode = {
            label: this.titleCase.transform(combinedLabel),
            data: obj,
            expandedIcon: "fa fa-folder-open",
            collapsedIcon: "fa fa-folder",
            children: []
          };

          if (obj.hasOwnProperty("children")) {
            treeNode.children = this.convertToLongLabelTree(labelProperties, obj.children);
          } else {
            console.warn(`Please ensure the object you are converting to a TreeNode[] contains the property children'`);
          }
          fullTree.push(treeNode);
      });
    } else {
      console.warn("Only array objects can be converted to TreeNode[]. Please pass in an array object.");
    }
    return fullTree;
  }

  convertToTree(labelProperty: string, dataObjects: any, childLabelProperty?: string, childObjectLabel?: string): TreeNode[] {
    let fullTree: TreeNode[] = [];

    if (Array.isArray(dataObjects)) {
      dataObjects.forEach(obj => {
        if (obj.hasOwnProperty(labelProperty)) {
          // Storing the expanded value in a constant and deleting it, since expanded is now moved inside data object.
          const expanded = obj.expanded;
          delete obj.expanded;
          // obj[labelProperty] to string in case number is passed on
          let treeNode: TreeNode = {
            label: this.titleCase.transform(obj[labelProperty].toString()),
            data: obj,
            expanded: expanded,
            expandedIcon: "fa fa-folder-open",
            collapsedIcon: "fa fa-folder",
            children: []
          };

          if (isNullOrUndefined(childLabelProperty) && isNullOrUndefined(childObjectLabel)) {
            if (obj.hasOwnProperty("children")) {
              treeNode.children = this.convertToTree(labelProperty, obj.children);
            } else {
              console.warn(`Please ensure the object you are converting to a TreeNode[] contains the property
          'children'`);
            }
          } else if (!isNullOrUndefined(childLabelProperty) && !isNullOrUndefined(childObjectLabel)) {
            treeNode.children = this.convertToTree(childLabelProperty, obj[childObjectLabel]);
          } else {
            console.warn(`Please ensure that if providing a childLabel, the childValue object must be set as well`);
          }

          fullTree.push(treeNode);
        } else {
          console.warn(`Please ensure the object you are converting to a TreeNode[] contains the property
          '${labelProperty}'`);
        }
      });
    } else {
      console.warn("Only array objects can be converted to TreeNode[]. Please pass in an array object.");
    }
    return fullTree;
  }

  /**
   * @desc this method loops through each field in object passed and forms up a custom search query url
   * @param searchObject - custom search fields that user inputs
   */
  buildSearchPanelURL(searchObject): string {
    let result: string = '';
    for (let key in searchObject) {
      let value = searchObject[key];
      console.log(value);
      if (key != null && value != null) {
        result = result.concat(`&${key}=${value}`);
      }
    }
    return result;
  }

  /**
   * @desc this method loops through values and forms up a custom query url
   *        eg: paramName=value1&paramName=value2..
   * @param paramName - paramName that should be attached to each value
   * @param values - array of values
   */
  buildObjectAsListURL(paramName: string, values: any[]) {
    let result: string = '';
    for (let key in values) {
      result = result.concat(`&${paramName}=${values[key]}`);
    }
    return result;
  }

  sortMonthDropdown(monthDropdown: SelectItem[]): SelectItem[] {
    return monthDropdown.sort(function (a, b) {
      var MONTH = {Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11};
      return MONTH[a.label] - MONTH[b.label];
    });
  }

  /**
   * Filters the dropdown with any
   */
  private filterExcludedOptionsFromDropdown(enumDropDownArray: SelectItem[], excludedObjects: any[]) {

    if (excludedObjects && excludedObjects.length > 0) {
      let filteredOptions: SelectItem[] = [];
      enumDropDownArray.forEach((dropDownOption, index) => {
        if (!excludedObjects.includes(dropDownOption.value)) {
          filteredOptions.push(dropDownOption);
        }
      });
      return filteredOptions;
    } else {
      return enumDropDownArray;
    }
  }

  private getReplaceLabel(labelChangeList: LabelChange[], label: string): string {
    let labelPair: LabelChange = null;

    if (labelChangeList) {
      labelPair = labelChangeList.find(labelPair => labelPair.label == label);
    }
    return labelPair == null ? null : labelPair.replace;
  }


}
